/**
 * Janus lane MV1 · M3d — the BURROW ROUTE rule (owner rulings 1, 4, 7: plans/burrow-and-blind-teleport-solid-rules-2026-09-24.md;
 * design plans/janus-m4b-teleport-m3d-burrow-plan-2026-09-24.md §3). PHP twin: ft_burrow_route_verdict() in
 * includes/functions/janus/encounter/solid_material.php — both must return the same verdict (parity:
 * tools/test/janus-solid-material-php-ts-parity.mjs). Built on shared/solid-material.ts.
 *
 * A burrow is 1:1 feet along a 3D polyline, the body (vertical cylinder, feet at z) resampled every foot of arc
 * (burrowSamples — the ft3d_resample_spatial_polyline rule). Per foot:
 *  · SOLIDS THE BODY SWEEPS: wall slabs and painted ribbons by a swept test along the REAL path between consecutive samples
 *    (through every polyline vertex in between — a corner is never cut); the ground (box air / earth / stone / worked below
 *    floors and above roofs) by probing the body's axis and 16 rim points at feet / middle / head. Anything the creature's
 *    tier cannot dig → REFUSED, naming the material.
 *  · CONTACT (ruling 4): the body touches BURROWABLE solid, or burrowable solid lies within 2.5 ft of it (a shell 2.5 ft
 *    out from the rim at the body's own heights, 2.5 ft below the feet and above the head, and the exact distance to each
 *    wall slab); open air beyond that → REFUSED.
 *  · Another body across the route → hand back (legacy prices creature space).
 * End: `inside` = the body overlaps solid (bodyInSolid); otherwise it must STAND free on a room floor (bodyFreeAt,
 * standing) — a blocked or floating emerge is REFUSED (ruling 7: no displacement on emerge).
 * Known conservative edges (Codex M3d review, logged): a slab sweep uses the 1 ft path piece's whole z span; ground probes
 * miss at most r·(1 − cos 11.25°) of rim overlap (≈ 0.1 ft for a Large body).
 */
import { bodyFreeAt, canBurrow, solidAt, SOLID_RANK, type BurrowTier, type SolidBody, type SolidContext, type SolidMaterial, type SolidSlab } from './solid-material.js';
import { wallIntersectsCylinder, wallIntersectsSweep } from './painted-wall-geometry.js';

export type BurrowPoint = [number, number, number];
export interface BurrowSample { x: number; y: number; z: number; seg: number }
export type BurrowVerdict =
  | { ok: true; samples: BurrowSample[]; inside: boolean; underground: boolean }
  | { ok: false; refused: string; at: number }
  | { ok: false; handback: string; at: number };
export const BURROW_CONTACT_FT = 2.5;
export const BURROW_CONTACT_MESSAGE = 'A burrow must stay in contact with earth or stone.';
export const BURROW_EMERGE_MESSAGE = 'You cannot emerge there.';
const EPS = 1e-9;
/** cos / sin of k·22.5°, the same literals in the PHP twin (never Math.cos drift between runtimes). */
const RIM: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [0.9238795325112867, 0.3826834323650898], [0.7071067811865476, 0.7071067811865476], [0.3826834323650898, 0.9238795325112867],
  [0, 1], [-0.3826834323650898, 0.9238795325112867], [-0.7071067811865476, 0.7071067811865476], [-0.9238795325112867, 0.3826834323650898],
  [-1, 0], [-0.9238795325112867, -0.3826834323650898], [-0.7071067811865476, -0.7071067811865476], [-0.3826834323650898, -0.9238795325112867],
  [0, -1], [0.3826834323650898, -0.9238795325112867], [0.7071067811865476, -0.7071067811865476], [0.9238795325112867, -0.3826834323650898],
];
const MATERIAL_NAME: Readonly<Record<SolidMaterial, string>> = { none: 'Open air', earth: 'Earth', stone: 'Stone', worked: 'Worked stone', adamantine: 'Adamantine', magical: 'A magical wall' };
const TIER_NAME: Readonly<Record<BurrowTier, string>> = { none: 'nothing', earth: 'earth', stone: 'earth and natural stone', worked: 'earth and stone' };
export function burrowMaterialMessage(material: SolidMaterial, tier: BurrowTier): string {
  return `${MATERIAL_NAME[material]} stops you — you can only burrow through ${TIER_NAME[tier]} here.`;
}
/** PHP is_numeric() twin: a number or a non-empty numeric string — never null / '' / false read as 0. */
const NUMERIC = /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/;
function numeric(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && NUMERIC.test(v)) { const n = Number(v); return Number.isFinite(n) ? n : null; }
  return null;
}
/** A polyline point as three finite numbers, or null. */
export function burrowPoint(p: unknown): BurrowPoint | null {
  if (!Array.isArray(p) || p.length < 3) return null;
  const x = numeric(p[0]), y = numeric(p[1]), z = numeric(p[2]);
  return x === null || y === null || z === null ? null : [x, y, z];
}

/** ft3d_resample_spatial_polyline twin (the same rule as traversal-3d.ts spatialSamples; re-stated so the PHP twin is one file). */
export function burrowSamples(points: ReadonlyArray<unknown>): BurrowSample[] {
  const clean: BurrowPoint[] = [], original: number[] = [];
  points.forEach((raw, index) => {
    const next = burrowPoint(raw);
    if (!next) return;
    const last = clean[clean.length - 1];
    if (last && Math.hypot(Math.hypot(next[0] - last[0], next[1] - last[1]), next[2] - last[2]) < EPS) return;
    clean.push(next); original.push(index);
  });
  if (clean.length <= 1) return clean.map(p => ({ x: p[0], y: p[1], z: p[2], seg: 0 }));
  const out: BurrowSample[] = [{ x: clean[0]![0], y: clean[0]![1], z: clean[0]![2], seg: 0 }];
  let carry = 0;
  for (let i = 1; i < clean.length; i++) {
    const from = clean[i - 1]!, to = clean[i]!;
    const dx = to[0] - from[0], dy = to[1] - from[1], dz = to[2] - from[2];
    const length = Math.hypot(Math.hypot(dx, dy), dz);
    if (length < EPS) continue;
    const previous = clean[i - 2];
    if (previous && (Math.abs(previous[2] - from[2]) > EPS) !== (Math.abs(dz) > EPS)) {
      const last = out[out.length - 1]!;
      if (Math.hypot(Math.hypot(last.x - from[0], last.y - from[1]), last.z - from[2]) > EPS) out.push({ x: from[0], y: from[1], z: from[2], seg: original[i - 1]! });
      carry = 0;
    }
    let travelled = 0;
    while (carry + (length - travelled) >= 1 - EPS) {
      travelled += 1 - carry;
      carry = 0;
      const ratio = travelled / length;
      out.push({ x: from[0] + dx * ratio, y: from[1] + dy * ratio, z: from[2] + dz * ratio, seg: original[i]! });
    }
    carry += length - travelled;
  }
  const end = clean[clean.length - 1]!;
  if (carry > EPS) out.push({ x: end[0], y: end[1], z: end[2], seg: original[clean.length - 1]! });
  else out[out.length - 1] = { x: end[0], y: end[1], z: end[2], seg: out[out.length - 1]!.seg };
  return out;
}

/** The body's own probes: the axis and 16 rim points 0.01 ft inside the rim (touching a face is not inside it), at feet / middle / head. */
function bodyProbes(s: BurrowSample, radius: number, height: number): BurrowPoint[] {
  const r = Math.max(0, radius - 0.01);
  const out: BurrowPoint[] = [];
  for (const z of [s.z + 0.01, s.z + height / 2, s.z + height - 0.01]) {
    out.push([s.x, s.y, z]);
    for (const [c, n] of RIM) out.push([s.x + r * c, s.y + r * n, z]);
  }
  return out;
}
/** The contact shell: 2.5 ft out from the rim at the body's own heights, and the axis + rim 2.5 ft below the feet and above
 *  the head — every probe exactly 2.5 ft from the body (never a 3.5 ft corner). */
function shellProbes(s: BurrowSample, radius: number, height: number): BurrowPoint[] {
  const out: BurrowPoint[] = [];
  const far = radius + BURROW_CONTACT_FT, near = Math.max(0, radius - 0.01);
  for (const z of [s.z + 0.01, s.z + height / 2, s.z + height - 0.01]) for (const [c, n] of RIM) out.push([s.x + far * c, s.y + far * n, z]);
  for (const z of [s.z - BURROW_CONTACT_FT, s.z + height + BURROW_CONTACT_FT]) {
    out.push([s.x, s.y, z]);
    for (const [c, n] of RIM) out.push([s.x + near * c, s.y + near * n, z]);
  }
  return out;
}

/** The slab's solid pieces along its span over a height range (openings spanning the whole range cut out). */
function slabPiecesSpanning(slab: SolidSlab, zLow: number, zHigh: number): Array<[number, number]> {
  let pieces: Array<[number, number]> = [[slab.span.min, slab.span.max]];
  for (const opening of slab.openings) {
    if (zLow < opening.zmin - 1e-6 || zHigh > opening.zmax + 1e-6) continue;
    const next: Array<[number, number]> = [];
    for (const [lo, hi] of pieces) {
      if (opening.max <= lo || opening.min >= hi) { next.push([lo, hi]); continue; }
      if (opening.min - lo > 1e-6) next.push([lo, Math.min(hi, opening.min)]);
      if (hi - opening.max > 1e-6) next.push([Math.max(lo, opening.max), hi]);
    }
    pieces = next;
  }
  return pieces;
}
/** Planar distance from the body's AXIS to the slab's nearest solid piece over the body's height, and the vertical gap. */
function slabDistance(slab: SolidSlab, s: BurrowSample, height: number): { planar: number; vertical: number } {
  const zLow = s.z, zHigh = s.z + height;
  const vertical = Math.max(0, slab.z.min - zHigh, zLow - slab.z.max);
  const lo = Math.max(zLow, slab.z.min), hi = Math.min(zHigh, slab.z.max);
  const pieces = hi > lo ? slabPiecesSpanning(slab, lo, hi) : [[slab.span.min, slab.span.max] as [number, number]];
  const normal = slab.axis === 'x' ? s.x : s.y, along = slab.axis === 'x' ? s.y : s.x;
  const across = Math.max(0, Math.abs(normal - slab.at) - slab.half);
  let planar = Infinity;
  for (const [a, b] of pieces) planar = Math.min(planar, Math.hypot(across, Math.max(0, a - along, along - b)));
  return { planar, vertical };
}

/** Does the body at s overlap solid? Slabs and ribbons exactly (bodyFreeAt's own tests); the ground by the body probes.
 *  The same test decides a route's end state and the Burrowed reconciliation (ft_creature_in_solid). */
export function bodyInSolid(ctx: SolidContext, s: BurrowSample, radius: number, height: number): boolean {
  for (const slab of ctx.slabs) {
    if (!(s.z < slab.z.max - 1e-6 && s.z + height > slab.z.min + 1e-6)) continue;
    if (slabDistance(slab, s, height).planar < radius - 1e-6) return true;
  }
  for (const ribbon of ctx.ribbons) if (wallIntersectsCylinder(ribbon, { x: s.x, y: s.y, z: s.z }, radius, height)) return true;
  return bodyProbes(s, radius, height).some(p => solidAt(ctx, p[0], p[1], p[2]) !== 'none');
}

/** Planar distance from segment a→b to the axis-aligned rectangle [x0,x1]×[y0,y1] (0 when they touch). */
export function segmentRectDistance(ax: number, ay: number, bx: number, by: number, x0: number, x1: number, y0: number, y1: number): number {
  const inside = (x: number, y: number): boolean => x >= x0 && x <= x1 && y >= y0 && y <= y1;
  if (inside(ax, ay) || inside(bx, by)) return 0;
  const cross = (px: number, py: number, qx: number, qy: number, rx: number, ry: number, sx: number, sy: number): boolean => {
    const d = (qx - px) * (sy - ry) - (qy - py) * (sx - rx);
    if (Math.abs(d) < 1e-12) return false;
    const t = ((rx - px) * (sy - ry) - (ry - py) * (sx - rx)) / d, u = ((rx - px) * (qy - py) - (ry - py) * (qx - px)) / d;
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  };
  const edges: Array<[number, number, number, number]> = [[x0, y0, x1, y0], [x1, y0, x1, y1], [x1, y1, x0, y1], [x0, y1, x0, y0]];
  if (edges.some(([rx, ry, sx, sy]) => cross(ax, ay, bx, by, rx, ry, sx, sy))) return 0;
  const pointSeg = (px: number, py: number, qx: number, qy: number, rx: number, ry: number): number => {
    const dx = rx - qx, dy = ry - qy, len = dx * dx + dy * dy;
    const t = len < 1e-12 ? 0 : Math.max(0, Math.min(1, ((px - qx) * dx + (py - qy) * dy) / len));
    return Math.hypot(px - qx - t * dx, py - qy - t * dy);
  };
  const clampDist = (px: number, py: number): number => Math.hypot(Math.max(x0 - px, 0, px - x1), Math.max(y0 - py, 0, py - y1));
  return Math.min(clampDist(ax, ay), clampDist(bx, by), ...[[x0, y0], [x1, y0], [x1, y1], [x0, y1]].map(([cx, cy]) => pointSeg(cx!, cy!, ax, ay, bx, by)));
}

/** The slab materials the body SWEEPS along one straight piece of the path. */
function sweptSlabs(slabs: readonly SolidSlab[], a: BurrowPoint, b: BurrowPoint, radius: number, height: number): SolidMaterial[] {
  const out: SolidMaterial[] = [];
  const zLow = Math.min(a[2], b[2]), zHigh = Math.max(a[2], b[2]) + height;
  for (const slab of slabs) {
    if (!(zLow < slab.z.max - 1e-6 && zHigh > slab.z.min + 1e-6)) continue;
    for (const [s0, s1] of slabPiecesSpanning(slab, Math.max(zLow, slab.z.min), Math.min(zHigh, slab.z.max))) {
      const rect = slab.axis === 'x' ? [slab.at - slab.half, slab.at + slab.half, s0, s1] : [s0, s1, slab.at - slab.half, slab.at + slab.half];
      if (segmentRectDistance(a[0], a[1], b[0], b[1], rect[0]!, rect[1]!, rect[2]!, rect[3]!) < radius - 1e-6) { out.push(slab.material); break; }
    }
  }
  return out;
}

/** The whole route's verdict. `stopIndex` (a truncated walk's paid stop) ends the judgement early; default = the end. */
export function burrowRouteVerdict(ctx: SolidContext, bodies: readonly SolidBody[], tier: BurrowTier, polyline: ReadonlyArray<unknown>,
  radius: number, height: number, stopIndex?: number): BurrowVerdict {
  const samples = burrowSamples(polyline);
  if (samples.length < 2) return { ok: false, handback: 'route-or-movement-mode', at: 0 };
  const last = stopIndex === undefined ? samples.length - 1 : Math.max(0, Math.min(samples.length - 1, stopIndex));
  let inside = false, underground = false;
  for (let i = 0; i <= last; i++) {
    const s = samples[i]!;
    const found: SolidMaterial[] = [];
    if (i > 0) {
      // The REAL path from the previous sample: through every polyline vertex in between (a corner is never cut).
      const a = samples[i - 1]!;
      const path: BurrowPoint[] = [[a.x, a.y, a.z]];
      for (let k = Math.max(a.seg, 0); k < s.seg; k++) { const v = burrowPoint(polyline[k]); if (v) path.push(v); }
      path.push([s.x, s.y, s.z]);
      for (let k = 1; k < path.length; k++) {
        const p = path[k - 1]!, q = path[k]!;
        found.push(...sweptSlabs(ctx.slabs, p, q, radius, height));
        for (const ribbon of ctx.ribbons) {
          if (wallIntersectsSweep(ribbon, { x: p[0], y: p[1], z: p[2] }, { x: q[0], y: q[1], z: q[2] }, radius, height)) { found.push('magical'); break; }
        }
      }
    }
    const body = bodyProbes(s, radius, height).map(p => solidAt(ctx, p[0], p[1], p[2]));
    found.push(...body);
    let worst: SolidMaterial = 'none';
    for (const m of found) if (!canBurrow(tier, m) && SOLID_RANK[m] > SOLID_RANK[worst]) worst = m;
    if (worst !== 'none') return { ok: false, refused: burrowMaterialMessage(worst, tier), at: i };
    const diggable = (m: SolidMaterial): boolean => m !== 'none' && canBurrow(tier, m);
    let touching = body.some(diggable);
    if (!touching) {
      for (const slab of ctx.slabs) {
        if (!canBurrow(tier, slab.material)) continue;
        const d = slabDistance(slab, s, height);
        if (Math.hypot(Math.max(0, d.planar - radius), d.vertical) <= BURROW_CONTACT_FT + 1e-6) { touching = true; break; }
      }
    }
    const shell = touching ? [] : shellProbes(s, radius, height).map(p => solidAt(ctx, p[0], p[1], p[2]));
    if (!touching) touching = shell.some(diggable);
    if (!touching) {
      // Only undiggable solid nearby (an earth burrower on a stone floor): name it — that is why the burrow cannot start.
      let near: SolidMaterial = 'none';
      for (const m of shell) if (m !== 'none' && SOLID_RANK[m] > SOLID_RANK[near]) near = m;
      return { ok: false, refused: near === 'none' ? BURROW_CONTACT_MESSAGE : burrowMaterialMessage(near, tier), at: i };
    }
    for (const other of bodies) {
      if (!(s.z < other.z + other.height - 1e-6 && s.z + height > other.z + 1e-6)) continue;
      if (Math.hypot(s.x - other.x, s.y - other.y) < radius + other.radius - 1e-6) return { ok: false, handback: 'creature-space', at: i };
    }
    const buried = bodyInSolid(ctx, s, radius, height);
    if (buried) underground = true;
    if (i === last) inside = buried;
  }
  const end = samples[last]!;
  if (!inside && !bodyFreeAt(ctx, bodies, end.x, end.y, end.z, radius, height, true)) return { ok: false, refused: BURROW_EMERGE_MESSAGE, at: last };
  return { ok: true, samples: samples.slice(0, last + 1), inside, underground };
}
